import { Box, CircularProgress, IconButton } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useEffect, useMemo, useState } from 'react';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { useRoomContext } from 'contexts/RoomContext';
import { useFlash } from 'contexts/flash';
import {
  createAssetIssueSession,
  notifyAssetIssue,
  updateAssetIssueSession
} from 'services/report';
import { PhotoManagement } from 'app/pages/ValidationsPage/components/elements/PhotoManagement';
import type { AdHocSession } from 'types/report';
import { IssueAssignmentFields } from './IssueAssignmentFields';
import { IssueNotifyModal } from './IssueNotifyModal';
import { IssueSessionsSection } from './IssueSessionsSection';
import { AssetIssueLegacyFields } from './AssetIssueLegacyFields';
import {
  IssueDialogSection,
  IssueMetaGrid,
  IssuePhotosGrid,
  IssueWorkflowDialogLayout
} from './IssueWorkflowDialogLayout';
import { useAssetIssueDialog } from '../hooks/useAssetIssueDialog';
import { filterIssueNotificationUsers, resolveIssueClientId } from '../utils/issueNotifyUsers';

export const AssetIssueDialog = ({
  isOpen,
  issueId,
  onClose,
  onSaved
}: {
  isOpen: boolean;
  issueId?: number;
  onClose: () => void;
  onSaved?: () => void;
}) => {
  const { users } = useRoomContext();
  const { setFlash } = useFlash();
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [selectedNotificationUsers, setSelectedNotificationUsers] = useState<number[]>([]);
  const [selectedNotificationChannels, setSelectedNotificationChannels] = useState<string[]>([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  const {
    loading,
    issue,
    isEditing,
    issuesConfig,
    criticalityLevels,
    submitId,
    submittedAssetId,
    legacyForm,
    assignmentForm,
    existingBeforePhotos,
    existingAfterPhotos,
    newBeforePhotos,
    setNewBeforePhotos,
    newAfterPhotos,
    setNewAfterPhotos,
    deletedPhotoIds,
    setDeletedPhotoIds,
    handleSave,
    setIssue
  } = useAssetIssueDialog({ isOpen, issueId, onSaved });

  const issueClientId = useMemo(() => resolveIssueClientId(issue), [issue]);

  const notificationUserOptions = useMemo(
    () => filterIssueNotificationUsers(users, issueClientId),
    [users, issueClientId]
  );

  useEffect(() => {
    const allowedIds = new Set(notificationUserOptions.map((u) => Number(u.value)));
    setSelectedNotificationUsers((prev) => prev.filter((id) => allowedIds.has(Number(id))));
  }, [notificationUserOptions]);

  useEffect(() => {
    if (!isOpen) {
      setIsNotifyModalOpen(false);
      setNotificationMessage('');
      setSelectedNotificationUsers([]);
      setSelectedNotificationChannels([]);
    }
  }, [isOpen]);

  const handleSendManualNotification = async () => {
    if (!submitId || !submittedAssetId || !issueId) return;
    if (!selectedNotificationUsers.length || !selectedNotificationChannels.length) {
      setFlash({ type: 'error', message: 'Select users and channels.' });
      return;
    }
    setIsSendingNotification(true);
    try {
      const res = await notifyAssetIssue(submitId, submittedAssetId, issueId, {
        userIds: selectedNotificationUsers,
        channels: selectedNotificationChannels,
        message: notificationMessage
      });
      setFlash({ type: 'success', message: res?.message || 'Notifications sent.' });
      setIsNotifyModalOpen(false);
      setNotificationMessage('');
      setSelectedNotificationUsers([]);
      setSelectedNotificationChannels([]);
    } catch {
      setFlash({ type: 'error', message: 'Failed to send notifications.' });
    } finally {
      setIsSendingNotification(false);
    }
  };

  const sessions = (issue?.sessions as AdHocSession[] | undefined) ?? [];

  return (
    <>
      <DialogConfirm
        isOpen={isOpen}
        onClose={onClose}
        textTitle="Asset Issue"
        textButton={isEditing ? 'Save' : 'Edit'}
        textCancel="Close"
        onSubmit={handleSave}
        isIconClose
        maxWidth={1280}
        headerActions={
          <IconButton
            onClick={() => setIsNotifyModalOpen(true)}
            sx={{ color: '#2B3492', p: 0.5 }}
            title="Manual Notification">
            <NotificationsActiveIcon />
          </IconButton>
        }>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <IssueWorkflowDialogLayout
            main={
              <>
                <IssueDialogSection title="Issue Details">
                  <IssueMetaGrid
                    items={[
                      { label: 'Client', value: issue?.room?.client?.clientName },
                      { label: 'Location', value: issue?.room?.name },
                      { label: 'Assigned By', value: issue?.assignedByUser?.name }
                    ]}
                  />
                  <AssetIssueLegacyFields form={legacyForm} isEditing={isEditing} />
                </IssueDialogSection>

                <IssueDialogSection title="Assignment Information">
                  <IssueAssignmentFields
                    control={assignmentForm.control}
                    isEditing={isEditing}
                    users={users || []}
                    criticalityLevels={criticalityLevels}
                  />
                </IssueDialogSection>

                <IssueDialogSection title="Photo Documentation">
                  <IssuePhotosGrid>
                    <PhotoManagement
                      sectionLabel="Issue Photos Before"
                      existingPhotos={existingBeforePhotos}
                      newPhotos={newBeforePhotos}
                      deletedPhotoIds={deletedPhotoIds}
                      disabled={!isEditing}
                      onAddPhotos={(files) => setNewBeforePhotos((prev) => [...prev, ...files])}
                      onDeleteExistingPhoto={(photoId) =>
                        photoId > 0 &&
                        setDeletedPhotoIds((prev) =>
                          prev.includes(photoId) ? prev : [...prev, photoId]
                        )
                      }
                      onRestorePhoto={(photoId) =>
                        setDeletedPhotoIds((prev) => prev.filter((id) => id !== photoId))
                      }
                      onRemoveNewPhoto={(index) =>
                        setNewBeforePhotos((prev) => prev.filter((_, i) => i !== index))
                      }
                      compactUpload
                    />
                    <PhotoManagement
                      sectionLabel="Issue Photos After"
                      existingPhotos={existingAfterPhotos}
                      newPhotos={newAfterPhotos}
                      deletedPhotoIds={deletedPhotoIds}
                      disabled={!isEditing}
                      onAddPhotos={(files) => setNewAfterPhotos((prev) => [...prev, ...files])}
                      onDeleteExistingPhoto={(photoId) =>
                        photoId > 0 &&
                        setDeletedPhotoIds((prev) =>
                          prev.includes(photoId) ? prev : [...prev, photoId]
                        )
                      }
                      onRestorePhoto={(photoId) =>
                        setDeletedPhotoIds((prev) => prev.filter((id) => id !== photoId))
                      }
                      onRemoveNewPhoto={(index) =>
                        setNewAfterPhotos((prev) => prev.filter((_, i) => i !== index))
                      }
                      compactUpload
                    />
                  </IssuePhotosGrid>
                </IssueDialogSection>
              </>
            }
            sessions={
              submitId && submittedAssetId && issueId ? (
                <IssueSessionsSection
                  sessions={sessions}
                  users={users || []}
                  allowCreate
                  onCreateSession={async (payload) => {
                    const res = await createAssetIssueSession(
                      submitId,
                      submittedAssetId,
                      issueId,
                      payload
                    );
                    setIssue((prev) =>
                      prev ? { ...prev, sessions: [...(prev.sessions || []), res.session] } : prev
                    );
                    setFlash({ type: 'success', message: 'Session created.' });
                  }}
                  onSaveSession={async (sessionId, payload) => {
                    const res = await updateAssetIssueSession(
                      submitId,
                      submittedAssetId,
                      issueId,
                      sessionId,
                      payload
                    );
                    setIssue((prev) => {
                      if (!prev?.sessions) return prev;
                      return {
                        ...prev,
                        sessions: (prev.sessions as AdHocSession[]).map((s) =>
                          s.id === res.session.id ? res.session : s
                        )
                      };
                    });
                    setFlash({ type: 'success', message: 'Session updated.' });
                  }}
                />
              ) : undefined
            }
          />
        )}
      </DialogConfirm>

      <IssueNotifyModal
        isOpen={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
        onSubmit={handleSendManualNotification}
        disabled={isSendingNotification}
        issuesConfig={issuesConfig}
        userOptions={notificationUserOptions}
        selectedUserIds={selectedNotificationUsers}
        onSelectedUserIdsChange={setSelectedNotificationUsers}
        selectedChannels={selectedNotificationChannels}
        onSelectedChannelsChange={setSelectedNotificationChannels}
        message={notificationMessage}
        onMessageChange={setNotificationMessage}
      />
    </>
  );
};
